import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { useState } from "react";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { useReorderPolicyField, useUpdateReport } from "../data";
import { keysForDrop } from "./sortKeys";
import styles from "./PolicyFieldsCard.module.css";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import {
  TabView,
  TabPanel,
  TabPanelHeaderTemplateOptions,
} from "primereact/tabview";
import * as client from "../../../client";
import { readableNameFromPeril } from "../general-input/util";
import {
  FormattedValueField,
  FormattingTarget,
  useFormattedValue,
} from "./standardFormatting";
import {
  applyDeductibleAdd,
  applyDeductibleDelete,
  applyDeductibleEdit,
  applyDeductibleRestore,
  applyDeductibleRevert,
  applyLimitAdd,
  applyLimitDelete,
  applyLimitEdit,
  applyLimitRestore,
  applyLimitRevert,
  applySublimitAdd,
  applySublimitDelete,
  applySublimitEdit,
  applySublimitRestore,
  applySublimitRevert,
  FormExcerptParams,
} from "./policyEdits";
import { v4 as uuid } from "uuid";
import { effectiveExcerpts, RemovedExcerpt, removedExcerpts } from "./excerpts";
import { TermWordedValue, TermWordingNote } from "./termWording";
import { getPolicySourceNames } from "../utils";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../routes";
import {
  documentEditCounts,
  EditSection,
  excerptProvenance,
  manualEntryCount,
  Provenance,
  sectionEditCounts,
  sectionManualCount,
  sublimitProvenance,
  totalEdits,
} from "./provenance";

const PERIL_TYPES = Object.values(client.peril_type) as client.peril_type[];

const PERIL_OPTIONS: { label: string; value: client.peril_type }[] =
  PERIL_TYPES.map((p) => ({ label: readableNameFromPeril(p), value: p }));

// Canonical peril display order, mirroring the backend PerilType order, so the
// table groups perils the same way PolicyFly emits them (see ordered_perils).
const PERIL_RANK = new Map<string, number>(PERIL_TYPES.map((p, i) => [p, i]));

// Tab order in the TabView below.
const SECTION_BY_TAB: EditSection[] = ["limits", "sublimits", "deductibles"];

const SECTION_LABEL: Record<EditSection, string> = {
  limits: "limit",
  sublimits: "sublimit",
  deductibles: "deductible",
};

/** Modal shell with a Cancel/Confirm footer. Calls onClose (which should reset
 *  the caller's form state) on cancel, dismiss, or a successful confirm. */
const FormDialog: React.FC<{
  visible: boolean;
  header: string;
  canConfirm: boolean;
  /** A rejection keeps the dialog open and shows a standard formatting error. */
  onConfirm: () => Promise<void>;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ visible, header, canConfirm, onConfirm, onClose, children }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setError(null);
    onClose();
  };

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await onConfirm();
      close();
    } catch {
      setError(
        "Couldn't apply standard formatting. Try again, or untick it to save the value as typed."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      header={header}
      className={styles.formDialog}
      onHide={close}
      footer={
        <div>
          <Button label="Cancel" text severity="secondary" onClick={close} />
          <Button
            label="Confirm"
            disabled={!canConfirm}
            loading={saving}
            onClick={() => {
              if (canConfirm) void confirm();
            }}
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        {children}
        {error && <Message severity="error" text={error} />}
      </div>
    </Dialog>
  );
};

/** Small dialog component for adding a manual limit or deductible with PrimeReact controls. */
const AddManualEntryDialog: React.FC<{
  visible: boolean;
  header: string;
  field: "limit" | "deductible";
  namePlaceholder: string;
  descriptionPlaceholder: string;
  isDuplicateName: (peril: client.peril_type, name: string) => boolean;
  onHide: () => void;
  onConfirm: (
    peril: client.peril_type,
    name: string,
    description: string
  ) => void;
}> = ({
  visible,
  header,
  field,
  namePlaceholder,
  descriptionPlaceholder,
  isDuplicateName,
  onHide,
  onConfirm,
}) => {
  const [peril, setPeril] = useState<client.peril_type | null>(null);
  const [name, setName] = useState("");
  const description = useFormattedValue("", {
    field,
    classification: null,
  });

  const trimmedName = name.trim();
  // Block a name that collides case-insensitively with an existing entry in the
  // peril: adds join by casefolded name, so it would silently overwrite that
  // entry's value. The user edits the existing entry to change its value.
  const duplicate =
    peril != null && trimmedName !== "" && isDuplicateName(peril, trimmedName);

  const close = () => {
    setPeril(null);
    setName("");
    description.reset();
    onHide();
  };

  return (
    <FormDialog
      visible={visible}
      header={header}
      canConfirm={peril != null && trimmedName !== "" && !duplicate}
      onConfirm={async () => {
        if (peril) onConfirm(peril, trimmedName, await description.resolve());
      }}
      onClose={close}
    >
      <Dropdown
        options={PERIL_OPTIONS}
        optionLabel="label"
        optionValue="value"
        placeholder="Select peril type"
        className={styles.confirmDialogWidth}
        value={peril}
        onChange={(e) => setPeril(e.value)}
      />
      <InputText
        className={styles.confirmDialogWidth}
        placeholder={namePlaceholder}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <FormattedValueField
        state={description}
        placeholder={descriptionPlaceholder}
      />
      {duplicate && (
        <Message
          severity="warn"
          text={`A "${trimmedName}" entry already exists for this peril (names match ignoring case). Edit that entry to change its value.`}
        />
      )}
    </FormDialog>
  );
};

/** Controlled dialog for adding a manual sublimit. The category selects which
 *  classification block the row lands in ("Default" => null classification).
 *  Blocks confirming when the trimmed name is empty or duplicates an existing
 *  sublimit in the chosen category (which would otherwise collide on the table
 *  row key). */
const AddSublimitDialog: React.FC<{
  visible: boolean;
  isDuplicateName: (
    name: string,
    classification: client.Sublimit["classification"]
  ) => boolean;
  onHide: () => void;
  onConfirm: (
    name: string,
    value: string,
    classification: client.Sublimit["classification"]
  ) => void;
}> = ({ visible, isDuplicateName, onHide, onConfirm }) => {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<SublimitCategory>("default");

  const classification = classificationFromCategory(category);
  const value = useFormattedValue("", {
    field: "sublimit",
    classification: classification ?? null,
  });
  const trimmedName = name.trim();
  const duplicate =
    trimmedName !== "" && isDuplicateName(trimmedName, classification);

  const close = () => {
    setName("");
    value.reset();
    setCategory("default");
    onHide();
  };

  return (
    <FormDialog
      visible={visible}
      header="Add Sublimit"
      canConfirm={trimmedName !== "" && !duplicate}
      onConfirm={async () =>
        onConfirm(trimmedName, await value.resolve(), classification)
      }
      onClose={close}
    >
      <Dropdown
        className={styles.confirmDialogWidth}
        value={category}
        options={SUBLIMIT_CATEGORY_OPTIONS}
        onChange={(e) => setCategory(e.value)}
      />
      <InputText
        className={styles.confirmDialogWidth}
        placeholder="Sublimit name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <FormattedValueField state={value} placeholder="Sublimit value" />
      {duplicate && (
        <Message
          severity="warn"
          text={`A sublimit named "${trimmedName}" already exists in this classification.`}
        />
      )}
    </FormDialog>
  );
};

type EditRequest = {
  header: string;
  name: string;
  value: string;
  target: FormattingTarget;
  onAccept: (name: string, value: string) => void;
};

/** The shared name/value editor; reports the confirmed name and value. */
const EditValueDialog: React.FC<{
  request: EditRequest;
  onClose: () => void;
}> = ({ request, onClose }) => {
  const [name, setName] = useState(request.name);
  const value = useFormattedValue(request.value, request.target);
  return (
    <FormDialog
      visible
      header={request.header}
      canConfirm
      onConfirm={async () => request.onAccept(name, await value.resolve())}
      onClose={onClose}
    >
      <label>Name</label>
      <InputText
        className={styles.confirmDialogWidth}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <label>Value</label>
      <FormattedValueField state={value} />
    </FormDialog>
  );
};

type PillTone = "edited" | "added" | "manual" | "muted";

const Pill: React.FC<{ tone: PillTone; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <span className={`${styles.pill} ${styles[`pill_${tone}`]}`}>{children}</span>
);

const PROVENANCE_LABEL: Record<
  Exclude<Provenance, "extracted">,
  { tone: PillTone; label: string }
> = {
  edited: { tone: "edited", label: "Edited" },
  added: { tone: "added", label: "Added" },
  manual: { tone: "manual", label: "Manual" },
};

const ProvenancePill: React.FC<{ provenance: Provenance }> = ({
  provenance,
}) => {
  if (provenance === "extracted") return null;
  const { tone, label } = PROVENANCE_LABEL[provenance];
  return <Pill tone={tone}>{label}</Pill>;
};

/** Six-dot grip for the reorder handle. PrimeReact starts a drag only from
 *  the element carrying its handle class, so the wrapper names that class
 *  itself; the svg beneath it is not a pointer target (see the module css). */
const GRIP_ICON = (
  <span className="p-datatable-reorderablerow-handle">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
      <circle cx="4" cy="2.5" r="1.3" />
      <circle cx="8" cy="2.5" r="1.3" />
      <circle cx="4" cy="6" r="1.3" />
      <circle cx="8" cy="6" r="1.3" />
      <circle cx="4" cy="9.5" r="1.3" />
      <circle cx="8" cy="9.5" r="1.3" />
    </svg>
  </span>
);

/** Edit / revert / delete for one row. Revert only shows for an edited form
 *  row, where there is a form value to go back to. */
const RowActions: React.FC<{
  disabled: boolean;
  canRevert: boolean;
  onEdit: () => void;
  onRevert: () => void;
  onDelete: () => void;
}> = ({ disabled, canRevert, onEdit, onRevert, onDelete }) => (
  <div className={styles.rowActions}>
    <Button
      icon="pi pi-pencil"
      className={styles.iconButton}
      text
      disabled={disabled}
      aria-label="Edit"
      tooltip="Edit"
      tooltipOptions={{ showDelay: 400 }}
      onClick={onEdit}
    />
    {canRevert && (
      <Button
        icon="pi pi-replay"
        className={styles.iconButton}
        text
        disabled={disabled}
        aria-label="Revert to the form's value"
        tooltip="Revert to the form's value"
        tooltipOptions={{ showDelay: 400 }}
        onClick={onRevert}
      />
    )}
    <Button
      icon="pi pi-trash"
      severity="danger"
      className={styles.iconButton}
      text
      disabled={disabled}
      aria-label="Delete"
      tooltip="Delete"
      tooltipOptions={{ showDelay: 400 }}
      onClick={onDelete}
    />
  </div>
);

/** A removed row as the group notes list it. */
type RemovedItem = { name: string; value: string };

/** Notes under a group: rows removed from the form (expandable, each
 *  restorable) and a note when the manual section shadows the form's rows.
 *  Restore reports the item's index, since a form can repeat a name. */
const GroupNotes: React.FC<{
  removed: RemovedItem[];
  shadowed: number;
  itemLabel: string;
  expanded: boolean;
  disabled: boolean;
  onToggle: () => void;
  onRestore: (index: number) => void;
}> = ({
  removed,
  shadowed,
  itemLabel,
  expanded,
  disabled,
  onToggle,
  onRestore,
}) => (
  <div className={styles.groupNotes}>
    {shadowed > 0 && (
      <div className={styles.footerNote}>
        Manual entries shadow {shadowed} {itemLabel}
        {shadowed === 1 ? "" : "s"} from this form.
      </div>
    )}
    {removed.length > 0 && (
      <>
        <div className={styles.footerNote}>
          <span>{removed.length} removed from this form</span>
          <button
            type="button"
            className={styles.footerLink}
            onClick={onToggle}
          >
            {expanded ? "Hide" : "Show"}
          </button>
        </div>
        {expanded && (
          <div className={styles.removedList}>
            {removed.map((item, index) => (
              <div key={index} className={styles.removedRow}>
                <span className={styles.removedName}>{item.name}</span>
                <span>{item.value}</span>
                <button
                  type="button"
                  className={styles.footerLink}
                  disabled={disabled}
                  onClick={() => onRestore(index)}
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </>
    )}
  </div>
);

/** A group drawn below the table because none of its rows are visible, so
 *  the table has no group to hang its notes on. */
const EmptyGroup: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className={styles.emptyGroup}>
    <div className={styles.emptyGroupTitle}>{title}</div>
    {children}
  </div>
);

interface PolicyFieldsCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

/** One limit or deductible line as the table renders it. */
type ExcerptRow = {
  perilType: client.peril_type;
  readablePerilName: string;
  name: string;
  value: string;
  /** The form's value an edit replaced; null unless provenance is "edited". */
  original: string | null;
  provenance: Provenance;
  sortKey: string | null;
};

/** What a peril group's footer has to say. */
type GroupExtras = {
  removed: RemovedExcerpt[];
  /** Form rows hidden because the manual section holds this peril. */
  shadowed: number;
};

// Order excerpt rows: peril groups in canonical PerilType order (PERIL_RANK,
// matching the backend so the UI and PolicyFly group identically), then rows
// within a peril by their fractional key (unkeyed rows last).
const byPerilThenSortKey = (
  a: { perilType: client.peril_type; sortKey: string | null },
  b: { perilType: client.peril_type; sortKey: string | null }
): number => {
  const byPeril =
    (PERIL_RANK.get(a.perilType) ?? Number.MAX_SAFE_INTEGER) -
    (PERIL_RANK.get(b.perilType) ?? Number.MAX_SAFE_INTEGER);
  if (byPeril !== 0) return byPeril;
  // Keep any unranked perils grouped (don't interleave by sort_key across perils).
  if (a.perilType !== b.perilType) return a.perilType < b.perilType ? -1 : 1;
  if (a.sortKey === null && b.sortKey === null) return 0;
  if (a.sortKey === null) return 1;
  if (b.sortKey === null) return -1;
  return a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0;
};

const classificationOrder: Record<string, number> = {
  "": 0,
  time_limit: 1,
  distance_limit: 2,
  waiting_period: 3,
};

const readableClassification = (c: string | null | undefined): string => {
  switch (c) {
    case "time_limit":
      return "Time Limits";
    case "distance_limit":
      return "Distance Limits";
    case "waiting_period":
      return "Waiting Periods";
    default:
      return "Default";
  }
};

// A manual sublimit's category. "default" is the uncategorized bucket the
// backend represents as a null classification; the other values map straight
// through to their SublimitClassification.
type SublimitCategory =
  | NonNullable<client.Sublimit["classification"]>
  | "default";

const SUBLIMIT_CATEGORY_OPTIONS: { label: string; value: SublimitCategory }[] =
  [
    { label: "Default", value: "default" },
    { label: "Time Limit", value: "time_limit" },
    { label: "Distance Limit", value: "distance_limit" },
    { label: "Waiting Period", value: "waiting_period" },
  ];

const classificationFromCategory = (
  category: SublimitCategory
): client.Sublimit["classification"] =>
  category === "default" ? null : category;

type ExcerptEntry = client.AdvancedLimit | client.AdvancedDeductible;

/** Build flattened limit or deductible rows for a given policy, plus what each
 *  peril group's footer reports. Manual entries take precedence: if a peril
 *  exists in `manual`, the form's rows for that peril are shadowed (counted,
 *  not shown). Rows the user removed are listed per group for restoring. */
const buildExcerptRows = (
  extractedForPolicy: Record<string, ExcerptEntry>,
  manual: Record<string, ExcerptEntry>
): { rows: ExcerptRow[]; extras: Map<client.peril_type, GroupExtras> } => {
  const rows: ExcerptRow[] = [];
  const extras = new Map<client.peril_type, GroupExtras>();
  const addRows = (
    perilKey: string,
    entry: ExcerptEntry,
    manualPeril: boolean
  ): void => {
    for (const excerpt of effectiveExcerpts(entry)) {
      rows.push({
        perilType: perilKey as client.peril_type,
        readablePerilName: readableNameFromPeril(perilKey as client.peril_type),
        name: excerpt.name,
        value: excerpt.value,
        original: excerpt.original,
        provenance: excerptProvenance(excerpt, manualPeril),
        sortKey: excerpt.sortKey,
      });
    }
  };
  for (const [perilKey, entry] of Object.entries(extractedForPolicy)) {
    const peril = perilKey as client.peril_type;
    if (manual[perilKey] !== undefined) {
      extras.set(peril, {
        removed: [],
        shadowed: effectiveExcerpts(entry).length,
      });
      continue;
    }
    extras.set(peril, { removed: removedExcerpts(entry), shadowed: 0 });
    addRows(perilKey, entry, false);
  }
  for (const [perilKey, entry] of Object.entries(manual)) {
    addRows(perilKey, entry, true);
  }
  return { rows: rows.sort(byPerilThenSortKey), extras };
};

export const PolicyFieldsCard: React.FC<PolicyFieldsCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);
  const reorderMutation = useReorderPolicyField(
    specialtyPropertyInfo.report_id
  );
  // Disable actions (and dragging) while any save is in flight — including a
  // reorder, whose optimistic move has already landed but is still persisting.
  const isMutating = updateReport.isPending || reorderMutation.isPending;

  // --- Sublimit source data (not cloned, sublimits save immediately) ---
  const sublimitInfo = specialtyPropertyInfo.report_json.sublimit_info;
  const manualSublimits = sublimitInfo?.manual_sublimits ?? [];
  const extractedSublimitInfo = sublimitInfo?.extracted_sublimits ?? {};

  const deductibleInfo = specialtyPropertyInfo.report_json.deductible_info;
  const limitInfo = specialtyPropertyInfo.report_json.limit_info;

  // --- Policy selector ---
  const policyNames = getPolicySourceNames(
    sublimitInfo,
    deductibleInfo,
    limitInfo
  );
  const allPolicyValues = policyNames.map((name) => ({
    code: name,
    label: name,
  }));
  const [selectedExtractedPolicy, setSelectedExtractedPolicy] = useState(
    specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name &&
      policyNames.includes(
        specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name
      )
      ? {
          code: specialtyPropertyInfo.report_json.policy_info
            ?.selected_policy_form_name,
          label:
            specialtyPropertyInfo.report_json.policy_info
              ?.selected_policy_form_name,
        }
      : undefined
  );
  const policyCode = selectedExtractedPolicy?.code;

  const savedPolicyFormName =
    specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name;
  const policyFormMismatch =
    policyCode != null &&
    savedPolicyFormName != null &&
    policyCode !== savedPolicyFormName;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const activeSection: EditSection = SECTION_BY_TAB[activeTabIndex] ?? "limits";

  // Edits stored on each form, across all tabs, for the picker's pills.
  const editTotalsByForm = new Map(
    policyNames.map((name) => [
      name,
      totalEdits(
        documentEditCounts(name, sublimitInfo, deductibleInfo, limitInfo)
      ),
    ])
  );
  // The summary strip is scoped to the selected form and the open tab.
  const sectionCounts = policyCode
    ? sectionEditCounts(
        activeSection,
        policyCode,
        sublimitInfo,
        deductibleInfo,
        limitInfo
      )
    : { edited: 0, added: 0, removed: 0 };
  const sectionManual = sectionManualCount(
    activeSection,
    sublimitInfo,
    deductibleInfo,
    limitInfo
  );
  const manualCount = manualEntryCount(sublimitInfo, deductibleInfo, limitInfo);

  const [editRequest, setEditRequest] = useState<EditRequest | null>(null);
  const [addLimitVisible, setAddLimitVisible] = useState(false);
  const [addDeductibleVisible, setAddDeductibleVisible] = useState(false);
  const [addSublimitVisible, setAddSublimitVisible] = useState(false);
  // Group footers whose removed rows are listed, keyed "<tab>:<group>".
  const [shownRemoved, setShownRemoved] = useState<Set<string>>(new Set());
  const toggleRemoved = (key: string): void =>
    setShownRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const includedPerils = new Set(
    specialtyPropertyInfo.report_json.policy_info?.included_perils ?? []
  );
  const isPerilDisabled = (peril: client.peril_type): boolean =>
    !includedPerils.has(peril);

  // Rows show the text as it prints for the policy term; the edit dialogs
  // keep binding to the stored text.
  const termWording = specialtyPropertyInfo.term_wording;
  const renderValue = (stored: string, original: string | null) => (
    <span className={styles.valueCell}>
      <TermWordedValue termWording={termWording} stored={stored} />
      {original !== null && original !== stored && (
        <span className={styles.original}>{original}</span>
      )}
    </span>
  );

  // --- Sublimit rows ---
  const extractedSublimits = policyCode
    ? (extractedSublimitInfo[policyCode] ?? [])
    : [];

  const allSublimits = manualSublimits
    .map((limit) => ({ ...limit, manual: true }))
    .concat(
      extractedSublimits
        .map((limit) => ({ ...limit, manual: false }))
        .filter((limit) => !limit.is_removed)
    )
    // A row stored before rows had ids keys on its position, since a form can
    // repeat a name within a classification.
    .map((limit, index) => ({
      ...limit,
      classificationGroup: limit.classification ?? "",
      rowKey: limit.id ?? `${limit.classification ?? ""}::${index}`,
      provenance: sublimitProvenance(limit, limit.manual),
    }))
    .sort((a, b) => {
      const byClassification =
        (classificationOrder[a.classificationGroup] ?? 99) -
        (classificationOrder[b.classificationGroup] ?? 99);
      if (byClassification !== 0) return byClassification;
      // Within a classification block, manual and extracted rows interleave by
      // their fractional sort_key (unsorted rows last).
      const ak = a.sort_key ?? null;
      const bk = b.sort_key ?? null;
      if (ak === null && bk === null) return 0;
      if (ak === null) return 1;
      if (bk === null) return -1;
      return ak < bk ? -1 : ak > bk ? 1 : 0;
    });

  type SublimitRow = (typeof allSublimits)[number];

  // Removed form sublimits by classification group, for the group notes.
  const removedSublimitsByGroup = new Map<string, client.Sublimit[]>();
  for (const limit of extractedSublimits) {
    if (!limit.is_removed) continue;
    const group = limit.classification ?? "";
    removedSublimitsByGroup.set(group, [
      ...(removedSublimitsByGroup.get(group) ?? []),
      limit,
    ]);
  }
  // Classifications whose form rows are all removed have no group in the table.
  const visibleSublimitGroups = new Set(
    allSublimits.map((limit) => limit.classificationGroup)
  );
  const emptySublimitGroups = [...removedSublimitsByGroup.keys()]
    .filter((group) => !visibleSublimitGroups.has(group))
    .sort(
      (a, b) => (classificationOrder[a] ?? 99) - (classificationOrder[b] ?? 99)
    );

  // Drag-reorder a sublimit within its classification block (manual + extracted
  // merged). A drop may re-key rows from either source list, so emit one update
  // per list.
  const onSublimitRowReorder = (
    e: DataTableRowReorderEvent<SublimitRow[]>
  ): void => {
    if (e.dragIndex === e.dropIndex) return; // dropped in place — no move
    const dropped = e.value;
    const changes = keysForDrop(
      dropped,
      e.dropIndex,
      (a, b) => a.classificationGroup === b.classificationGroup,
      (row) => row.sort_key
    );
    if (changes.length === 0) return;
    const entriesFor = (manual: boolean) =>
      changes
        .filter((change) => change.item.manual === manual)
        .map((change) => ({
          id: change.item.id ?? null,
          sublimit_name: change.item.sublimit_name,
          classification: change.item.classification ?? null,
          sort_key: change.sortKey,
        }));
    const manualEntries = entriesFor(true);
    const extractedEntries = entriesFor(false);
    const sublimitOrder: client.SublimitSortUpdate[] = [];
    if (manualEntries.length > 0) {
      sublimitOrder.push({
        group: "manual",
        filename: null,
        entries: manualEntries,
      });
    }
    if (extractedEntries.length > 0) {
      sublimitOrder.push({
        group: "extracted",
        filename: policyCode ?? null,
        entries: extractedEntries,
      });
    }
    if (sublimitOrder.length > 0) reorderMutation.mutate({ sublimitOrder });
  };

  const currentSublimitInfo = (): client.SublimitInfo => ({
    extracted_sublimits: extractedSublimitInfo,
    manual_sublimits: manualSublimits,
  });

  const sublimitNotes = (group: string, removed: client.Sublimit[]) => {
    const key = `sublimits:${group}`;
    return (
      <GroupNotes
        removed={removed.map((limit) => ({
          name: limit.sublimit_name,
          value: limit.sublimit_description,
        }))}
        shadowed={0}
        itemLabel="sublimit"
        expanded={shownRemoved.has(key)}
        disabled={isMutating}
        onToggle={() => toggleRemoved(key)}
        onRestore={(index) => {
          const limit = removed[index];
          if (!limit) return;
          updateReport.mutate({
            sublimitInfo: applySublimitRestore(currentSublimitInfo(), {
              id: limit.id ?? null,
              name: limit.sublimit_name,
              classification: limit.classification ?? null,
              policyCode,
            }),
          });
        }}
      />
    );
  };

  // --- Deductible rows (derived from local state) ---
  const extractedDeductibles = deductibleInfo?.extracted_deductibles ?? {};
  const manualDeductibles = deductibleInfo?.manual_deductibles ?? {};
  const deductibles = buildExcerptRows(
    policyCode ? (extractedDeductibles[policyCode] ?? {}) : {},
    manualDeductibles
  );

  // --- Limit rows (derived from local state) ---
  const extractedLimitsAdvanced = limitInfo?.extracted_limits ?? {};
  const manualLimitsAdvanced = limitInfo?.manual_limits ?? {};
  const limits = buildExcerptRows(
    policyCode ? (extractedLimitsAdvanced[policyCode] ?? {}) : {},
    manualLimitsAdvanced
  );

  // Drag-reorder an excerpt row within its peril group. The peril resolves to
  // one entry (manual shadows extracted), so the new keys are written to that
  // entry's excerpt_sort_keys.
  const reorderExcerpts = (
    e: DataTableRowReorderEvent<ExcerptRow[]>,
    manual: Record<string, ExcerptEntry>,
    field: "limitOrder" | "deductibleOrder"
  ): void => {
    if (e.dragIndex === e.dropIndex) return; // dropped in place — no move
    const dropped = e.value;
    const dragged = dropped[e.dropIndex];
    if (dragged === undefined) return;
    const changes = keysForDrop(
      dropped,
      e.dropIndex,
      (a, b) => a.perilType === b.perilType,
      (r) => r.sortKey
    );
    if (changes.length === 0) return;
    const isManual = manual[dragged.perilType] != null;
    reorderMutation.mutate({
      [field]: [
        {
          group: isManual ? "manual" : "extracted",
          filename: isManual ? null : (policyCode ?? null),
          peril_type: dragged.perilType,
          excerpt_keys: Object.fromEntries(
            changes.map((change) => [change.item.name, change.sortKey])
          ),
        },
      ],
    });
  };

  const formExcerpt = (
    perilType: client.peril_type,
    name: string
  ): FormExcerptParams => ({ perilType, policyCode, name });

  // --- Limit and deductible handlers: persist immediately (triggers regen) ---
  const saveLimits = (advancedLimitInfo: client.AdvancedLimitInfo): void =>
    updateReport.mutate({ advancedLimitInfo, rerunReport: true });
  const saveDeductibles = (
    deductibleInfoNext: client.AdvancedDeductibleInfo
  ): void =>
    updateReport.mutate({
      deductibleInfo: deductibleInfoNext,
      rerunReport: true,
    });

  const limitHandlers = {
    edit: (row: ExcerptRow) =>
      setEditRequest({
        header: `Edit ${row.name}`,
        name: row.name,
        value: row.value,
        target: { field: "limit", classification: null },
        onAccept: (newName, newValue) =>
          saveLimits(
            applyLimitEdit(limitInfo, {
              perilType: row.perilType,
              policyCode,
              oldName: row.name,
              newName,
              newValue,
            })
          ),
      }),
    revert: (row: ExcerptRow) =>
      saveLimits(
        applyLimitRevert(limitInfo, formExcerpt(row.perilType, row.name))
      ),
    delete: (row: ExcerptRow) =>
      saveLimits(
        applyLimitDelete(limitInfo, {
          perilType: row.perilType,
          policyCode,
          name: row.name,
        })
      ),
    restore: (perilType: client.peril_type, name: string) =>
      saveLimits(applyLimitRestore(limitInfo, formExcerpt(perilType, name))),
  };

  const deductibleHandlers = {
    edit: (row: ExcerptRow) =>
      setEditRequest({
        header: `Edit ${row.name}`,
        name: row.name,
        value: row.value,
        target: { field: "deductible", classification: null },
        onAccept: (newName, newValue) =>
          saveDeductibles(
            applyDeductibleEdit(deductibleInfo, {
              perilType: row.perilType,
              policyCode,
              oldName: row.name,
              newName,
              newValue,
            })
          ),
      }),
    revert: (row: ExcerptRow) =>
      saveDeductibles(
        applyDeductibleRevert(
          deductibleInfo,
          formExcerpt(row.perilType, row.name)
        )
      ),
    delete: (row: ExcerptRow) =>
      saveDeductibles(
        applyDeductibleDelete(deductibleInfo, {
          perilType: row.perilType,
          policyCode,
          name: row.name,
        })
      ),
    restore: (perilType: client.peril_type, name: string) =>
      saveDeductibles(
        applyDeductibleRestore(deductibleInfo, formExcerpt(perilType, name))
      ),
  };

  /** The Limits and Deductibles tables share one shape. */
  const renderExcerptTable = (
    tabKey: "limits" | "deductibles",
    nameHeader: string,
    itemLabel: string,
    built: { rows: ExcerptRow[]; extras: Map<client.peril_type, GroupExtras> },
    manual: Record<string, ExcerptEntry>,
    handlers: typeof limitHandlers,
    orderField: "limitOrder" | "deductibleOrder"
  ) => {
    const perilNotes = (peril: client.peril_type, extras: GroupExtras) => {
      const key = `${tabKey}:${peril}`;
      return (
        <GroupNotes
          removed={extras.removed}
          shadowed={extras.shadowed}
          itemLabel={itemLabel}
          expanded={shownRemoved.has(key)}
          disabled={isMutating}
          onToggle={() => toggleRemoved(key)}
          onRestore={(index) => {
            const excerpt = extras.removed[index];
            if (excerpt) handlers.restore(peril, excerpt.name);
          }}
        />
      );
    };
    // Perils whose form rows are all removed have no group in the table.
    const visiblePerils = new Set(built.rows.map((row) => row.perilType));
    const emptyPerils = [...built.extras]
      .filter(
        ([peril, extras]) =>
          extras.removed.length > 0 && !visiblePerils.has(peril)
      )
      .sort(
        ([a], [b]) =>
          (PERIL_RANK.get(a) ?? Number.MAX_SAFE_INTEGER) -
          (PERIL_RANK.get(b) ?? Number.MAX_SAFE_INTEGER)
      );
    return (
      <>
        <DataTable
          value={built.rows}
          className={styles.policyTable}
          scrollable
          scrollHeight={"70vh"}
          size="small"
          rowGroupMode="subheader"
          groupRowsBy="readablePerilName"
          rowGroupHeaderTemplate={(data: ExcerptRow) => (
            <span
              className={`${styles.groupHeader} ${
                isPerilDisabled(data.perilType)
                  ? styles.disabledPerilGroupHeader
                  : ""
              }`}
            >
              {data.readablePerilName}
              {manual[data.perilType] !== undefined && (
                <Pill tone="manual">Manual</Pill>
              )}
            </span>
          )}
          rowGroupFooterTemplate={(data: ExcerptRow, options) => {
            const extras = built.extras.get(data.perilType);
            if (
              !extras ||
              (extras.removed.length === 0 && extras.shadowed === 0)
            )
              return null;
            return (
              <td colSpan={options.colSpan}>
                {perilNotes(data.perilType, extras)}
              </td>
            );
          }}
          rowClassName={(data: ExcerptRow) =>
            isPerilDisabled(data.perilType) ? styles.disabledPerilRow : ""
          }
          reorderableRows={!isMutating}
          onRowReorder={(e) => reorderExcerpts(e, manual, orderField)}
        >
          <Column
            rowReorder
            rowReorderIcon={GRIP_ICON}
            style={{ width: "2.5rem" }}
          />
          <Column
            header={nameHeader}
            className={styles.nameColumn}
            headerClassName={styles.nameColumn}
            body={(data: ExcerptRow) => (
              <span className={styles.nameCell}>
                {data.name}
                <ProvenancePill provenance={data.provenance} />
              </span>
            )}
          />
          <Column
            header="Value"
            body={(data: ExcerptRow) => renderValue(data.value, data.original)}
          />
          <Column
            header=""
            style={{ width: "7rem" }}
            body={(data: ExcerptRow) => (
              <RowActions
                disabled={isMutating}
                canRevert={data.provenance === "edited"}
                onEdit={() => handlers.edit(data)}
                onRevert={() => handlers.revert(data)}
                onDelete={() => handlers.delete(data)}
              />
            )}
          />
        </DataTable>
        {emptyPerils.map(([peril, extras]) => (
          <EmptyGroup key={peril} title={readableNameFromPeril(peril)}>
            {perilNotes(peril, extras)}
          </EmptyGroup>
        ))}
      </>
    );
  };

  const tabHeader =
    (label: string, count: number) =>
    (options: TabPanelHeaderTemplateOptions) => (
      <button
        type="button"
        role="tab"
        className={`${options.className} ${styles.tabHeader}`}
        aria-selected={options.selected}
        onClick={options.onClick}
        onKeyDown={options.onKeyDown}
      >
        <span className={options.titleClassName}>{label}</span>
        <span className={styles.tabCount}>{count}</span>
      </button>
    );

  const addButtons = [
    { label: "Add limit", open: () => setAddLimitVisible(true) },
    { label: "Add sublimit", open: () => setAddSublimitVisible(true) },
    { label: "Add deductible", open: () => setAddDeductibleVisible(true) },
  ];
  const addButton = addButtons[activeTabIndex];

  const docOption = (name: string) => (
    <span className={styles.docOption}>
      <span className={styles.docName}>{name}</span>
      {(editTotalsByForm.get(name) ?? 0) > 0 && (
        <Pill tone="edited">
          {editTotalsByForm.get(name)}{" "}
          {editTotalsByForm.get(name) === 1 ? "edit" : "edits"}
        </Pill>
      )}
    </span>
  );

  return (
    <Card>
      <div className={styles.content}>
        <div className={styles.toolbar}>
          <Dropdown
            value={selectedExtractedPolicy}
            options={allPolicyValues}
            onChange={(e) => setSelectedExtractedPolicy(e.value)}
            optionLabel="label"
            placeholder="Select a form"
            className={styles.docPicker}
            itemTemplate={(option: { label: string }) =>
              docOption(option.label)
            }
            valueTemplate={(option: { label: string } | null) =>
              option ? docOption(option.label) : <span>Select a form</span>
            }
            panelFooterTemplate={() =>
              manualCount > 0 ? (
                <div className={styles.docFooter}>
                  <Pill tone="manual">{manualCount} manual</Pill>
                  {manualCount === 1 ? "entry shows" : "entries show"} with
                  every form
                </div>
              ) : null
            }
          />
          <div className={styles.summary}>
            {policyCode && totalEdits(sectionCounts) === 0 && (
              <span>No {SECTION_LABEL[activeSection]} edits on this form</span>
            )}
            {sectionCounts.edited > 0 && (
              <Pill tone="edited">{sectionCounts.edited} edited</Pill>
            )}
            {sectionCounts.added > 0 && (
              <Pill tone="added">{sectionCounts.added} added</Pill>
            )}
            {sectionCounts.removed > 0 && (
              <Pill tone="muted">{sectionCounts.removed} removed</Pill>
            )}
            {sectionManual > 0 && (
              <>
                <span className={styles.summaryDivider} />
                <Pill tone="manual">{sectionManual} manual</Pill>
              </>
            )}
          </div>
        </div>
        {policyFormMismatch && (
          <Message
            severity="warn"
            className={styles.mismatch}
            content={
              <span>
                Selected form does not match the saved policy form "
                {savedPolicyFormName}". You can update it on the{" "}
                <Link
                  to={ROUTES.DASHBOARD_REPORT(
                    specialtyPropertyInfo.report_id,
                    "general-input"
                  )}
                >
                  General tab
                </Link>
                .
              </span>
            }
          />
        )}
        <div className={styles.termLine}>
          <TermWordingNote termWording={termWording} />
        </div>

        <div className={styles.tabs}>
          {addButton && (
            <Button
              size="small"
              outlined
              className={styles.addButton}
              disabled={isMutating}
              icon="pi pi-plus"
              label={addButton.label}
              onClick={addButton.open}
            />
          )}
          {editRequest && (
            <EditValueDialog
              request={editRequest}
              onClose={() => setEditRequest(null)}
            />
          )}
          <TabView
            activeIndex={activeTabIndex}
            onTabChange={(e) => setActiveTabIndex(e.index)}
          >
            <TabPanel headerTemplate={tabHeader("Limits", limits.rows.length)}>
              <AddManualEntryDialog
                visible={addLimitVisible}
                header="Add Limit"
                field="limit"
                namePlaceholder="Limit name"
                descriptionPlaceholder="Limit description"
                isDuplicateName={(peril, name) =>
                  limits.rows.some(
                    (row) =>
                      row.perilType === peril &&
                      row.name.toLowerCase() === name.toLowerCase()
                  )
                }
                onHide={() => setAddLimitVisible(false)}
                onConfirm={(peril, limitName, limitValue) =>
                  saveLimits(
                    applyLimitAdd(limitInfo, {
                      perilType: peril,
                      policyCode,
                      name: limitName,
                      value: limitValue,
                    })
                  )
                }
              />
              {renderExcerptTable(
                "limits",
                "Limit",
                "limit",
                limits,
                manualLimitsAdvanced,
                limitHandlers,
                "limitOrder"
              )}
            </TabPanel>
            <TabPanel
              headerTemplate={tabHeader("Sublimits", allSublimits.length)}
            >
              <AddSublimitDialog
                visible={addSublimitVisible}
                onHide={() => setAddSublimitVisible(false)}
                isDuplicateName={(name, classification) =>
                  // A duplicate is any existing row in the same classification
                  // with the same name (which would also collide on the table
                  // row key).
                  allSublimits.some(
                    (limit) =>
                      (limit.classification ?? null) === classification &&
                      limit.sublimit_name === name
                  )
                }
                onConfirm={(name, value, classification) => {
                  updateReport.mutate({
                    sublimitInfo: applySublimitAdd(currentSublimitInfo(), {
                      id: uuid(),
                      name,
                      value,
                      classification,
                    }),
                  });
                }}
              />
              <DataTable
                value={allSublimits}
                className={styles.policyTable}
                scrollable
                scrollHeight={"70vh"}
                size="small"
                dataKey="rowKey"
                rowGroupMode="subheader"
                groupRowsBy="classificationGroup"
                rowGroupHeaderTemplate={(data: SublimitRow) => (
                  <span className={styles.groupHeader}>
                    {readableClassification(data.classification)}
                  </span>
                )}
                rowGroupFooterTemplate={(data: SublimitRow, options) => {
                  const removed =
                    removedSublimitsByGroup.get(data.classificationGroup) ?? [];
                  if (removed.length === 0) return null;
                  return (
                    <td colSpan={options.colSpan}>
                      {sublimitNotes(data.classificationGroup, removed)}
                    </td>
                  );
                }}
                reorderableRows={!isMutating}
                onRowReorder={onSublimitRowReorder}
              >
                <Column
                  rowReorder
                  rowReorderIcon={GRIP_ICON}
                  style={{ width: "2.5rem" }}
                />
                <Column
                  header="Sublimit"
                  className={styles.nameColumn}
                  headerClassName={styles.nameColumn}
                  body={(data: SublimitRow) => (
                    <span className={styles.nameCell}>
                      {data.sublimit_name}
                      {data.document_excerpt && (
                        <TooltipWrapper
                          tooltipText={data.document_excerpt}
                          tooltipProps={{ position: "left" }}
                        >
                          <i
                            className={`pi pi-info-circle ${styles.excerptIcon}`}
                          />
                        </TooltipWrapper>
                      )}
                      <ProvenancePill provenance={data.provenance} />
                    </span>
                  )}
                />
                <Column
                  header="Value"
                  body={(data: SublimitRow) =>
                    renderValue(
                      data.manual_override ?? data.sublimit_description,
                      data.manual_override != null
                        ? data.sublimit_description
                        : null
                    )
                  }
                />
                <Column
                  header=""
                  style={{ width: "7rem" }}
                  body={(data: SublimitRow) => {
                    const ref = {
                      id: data.id ?? null,
                      name: data.sublimit_name,
                      classification: data.classification ?? null,
                    };
                    return (
                      <RowActions
                        disabled={isMutating}
                        canRevert={data.provenance === "edited"}
                        onEdit={() =>
                          setEditRequest({
                            header: "Edit Sublimit",
                            name: data.sublimit_name,
                            value:
                              data.manual_override ?? data.sublimit_description,
                            target: {
                              field: "sublimit",
                              classification: data.classification ?? null,
                            },
                            onAccept: (newName, newValue) => {
                              if (!data.manual && !policyCode) return;
                              updateReport.mutate({
                                sublimitInfo: applySublimitEdit(
                                  currentSublimitInfo(),
                                  {
                                    ...ref,
                                    manual: data.manual,
                                    policyCode,
                                    documentExcerpt:
                                      data.document_excerpt ?? null,
                                    manualRowId: uuid(),
                                    newName,
                                    newValue,
                                  }
                                ),
                              });
                            },
                          })
                        }
                        onRevert={() =>
                          updateReport.mutate({
                            sublimitInfo: applySublimitRevert(
                              currentSublimitInfo(),
                              { ...ref, policyCode }
                            ),
                          })
                        }
                        onDelete={() => {
                          if (!data.manual && !policyCode) return;
                          updateReport.mutate({
                            sublimitInfo: applySublimitDelete(
                              currentSublimitInfo(),
                              { ...ref, manual: data.manual, policyCode }
                            ),
                          });
                        }}
                      />
                    );
                  }}
                />
              </DataTable>
              {emptySublimitGroups.map((group) => (
                <EmptyGroup
                  key={group}
                  title={readableClassification(group || null)}
                >
                  {sublimitNotes(
                    group,
                    removedSublimitsByGroup.get(group) ?? []
                  )}
                </EmptyGroup>
              ))}
            </TabPanel>
            <TabPanel
              headerTemplate={tabHeader("Deductibles", deductibles.rows.length)}
            >
              <AddManualEntryDialog
                visible={addDeductibleVisible}
                header="Add Deductible"
                field="deductible"
                namePlaceholder="Deductible name"
                descriptionPlaceholder="Deductible description"
                isDuplicateName={(peril, name) =>
                  deductibles.rows.some(
                    (row) =>
                      row.perilType === peril &&
                      row.name.toLowerCase() === name.toLowerCase()
                  )
                }
                onHide={() => setAddDeductibleVisible(false)}
                onConfirm={(peril, deductibleName, deductibleValue) =>
                  saveDeductibles(
                    applyDeductibleAdd(deductibleInfo, {
                      perilType: peril,
                      policyCode,
                      name: deductibleName,
                      value: deductibleValue,
                    })
                  )
                }
              />
              {renderExcerptTable(
                "deductibles",
                "Deductible",
                "deductible",
                deductibles,
                manualDeductibles,
                deductibleHandlers,
                "deductibleOrder"
              )}
            </TabPanel>
          </TabView>
        </div>
      </div>
    </Card>
  );
};
